import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectCategoryLatestSync } from "store/sync-categories/SyncCategoriesSelectors";
import { selectCurrentCategory } from "store/category/CategorySelectors";
import { selectCurrentStoreId } from "store/store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import { useIntl } from "react-intl";
import {
  StyledNotificationPanel,
  TimestampSection,
  SourceTargetsSection,
  DetailLabel,
  DetailValue,
  VerticalText,
  useStyles,
  StyledBox,
} from "./SyncCatgeoriesNotificationStyles";
import { Box, Typography } from "@mui/material";
import ButtonComponent from "components/common/ButtonComponent";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

interface Props {
  open: boolean;
  onClose: () => void;
  onToggle: () => void;
}

const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const SyncCategoriesNotificationPanel: React.FC<Props> = ({
  open,
  onClose,
  onToggle,
}) => {
  const intl = useIntl();
  const categoryLatestSync = useSelector(selectCategoryLatestSync);
  const currentCategory = useSelector(selectCurrentCategory);
  const currentStoreId = useSelector(selectCurrentStoreId);
  const currentCatalogId = useSelector(selectCurrentCatalogId);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { buttonStyles, arrowStyles } = useStyles(open);

  // Auto-close after 10 seconds
  useEffect(() => {
    if (open && categoryLatestSync?.syncJobId) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        onClose();
      }, 10000);

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [open, categoryLatestSync?.syncJobId, onClose]);

  useEffect(() => {
    if (!open && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [open]);

  if (!categoryLatestSync?.syncJobId) {
    return null;
  }

  // Only show panel if current category is in source or target categories
  const isCurrentCategoryInSync =
    categoryLatestSync.source.categoryId === currentCategory ||
    categoryLatestSync.targets.some(
      (target) => target.categoryId === currentCategory,
    );

  // Don't show panel if current category is not involved in the sync
  if (!isCurrentCategoryInSync) {
    return null;
  }

  const isSourceDifferent =
    categoryLatestSync.source.storeId !== currentStoreId ||
    categoryLatestSync.source.catalogId !== currentCatalogId ||
    categoryLatestSync.source.categoryId !== currentCategory;

  return (
    <>
      <StyledNotificationPanel className={open ? "visible" : ""}>
        <ButtonComponent
          onClick={onToggle}
          style={buttonStyles}
          variant="contained"
          color="primary"
        >
          <StyledBox>
            <Typography style={arrowStyles}>
              {open ? (
                <KeyboardDoubleArrowRightIcon />
              ) : (
                <KeyboardDoubleArrowLeftIcon />
              )}
            </Typography>
            <VerticalText>
              {intl.formatMessage({
                id: "syncCategories.lastSyncLabel",
                defaultMessage: "Last Sync",
              })}
            </VerticalText>
          </StyledBox>
        </ButtonComponent>

        {open && (
          <>
            <Box>
              <DetailLabel variant="title">
                {intl.formatMessage({
                  id: "syncCategories.status",
                  defaultMessage: "Status:",
                })}
              </DetailLabel>
              <DetailValue variant="subtitle2">
                {categoryLatestSync.status}
              </DetailValue>
            </Box>

            <TimestampSection>
              <DetailLabel variant="title">
                {intl.formatMessage({
                  id: "syncCategories.lastSyncedAt",
                  defaultMessage: "Last synced at:",
                })}
              </DetailLabel>
              <DetailValue variant="subtitle2">
                {formatTimestamp(categoryLatestSync.statusAt)}
              </DetailValue>
            </TimestampSection>

            {isSourceDifferent ? (
              <SourceTargetsSection>
                <DetailLabel variant="title">
                  {intl.formatMessage({
                    id: "syncCategories.source",
                    defaultMessage: "Source:",
                  })}
                </DetailLabel>
                <DetailValue variant="subtitle2">
                  {categoryLatestSync.source.storeId} /{" "}
                  {categoryLatestSync.source.catalogId} /{" "}
                  {categoryLatestSync.source.categoryId}
                </DetailValue>
              </SourceTargetsSection>
            ) : (
              <SourceTargetsSection>
                <DetailLabel variant="title">
                  {intl.formatMessage({
                    id: "syncCategories.targets",
                    defaultMessage: "Targets:",
                  })}
                </DetailLabel>
                <DetailValue variant="subtitle2">
                  {categoryLatestSync.targets.map((target) => (
                    <Box key={target.categoryId}>
                      {target.storeId} / {target.catalogId} /{" "}
                      {target.categoryId}
                    </Box>
                  ))}
                </DetailValue>
              </SourceTargetsSection>
            )}
          </>
        )}
      </StyledNotificationPanel>
    </>
  );
};

export default SyncCategoriesNotificationPanel;
